// Filename: JobLoadingManager.ts

export interface Job {
    id: number | string;
    title: string;
    company: string;
    location: string;
    description: string;
    promoted?: boolean;
    jobType?: string | null;
    jobCategory?: string | null;
    workplaceType?: string | null;
    isRemote?: boolean;
    tags?: string[];
    benefits?: string[];
    salaryRange?: { min: string; max: string } | null;
    experienceLevel?: string | null;
    companyIndustry?: string | null;
    url?: string | null;
    shareUrl?: string | null;
    applyUrl?: string | null;
    instantApplyUrl?: string | null;
    instantApplyAvailable?: boolean;
    requiresApplicationAnswers?: boolean;
    requiredFields?: Array<{ name: string; label: string; required: boolean }>;
    applicationMode?: 'INTERNAL' | 'EXTERNAL' | 'INVALID' | string;
    applicationLabel?: string | null;
}

export interface JobSelectionOptions {
    pushHistory?: boolean;
    focusPreview?: boolean;
}

export interface JobHistoryState {
    fjJobDetail: true;
    jobId: string;
    resultsUrl: string;
    scrollY: number;
}

export interface JobResultsHistoryState {
    fjJobResults: true;
    scrollY: number;
}

interface JobLoadingStore {
    jobs: Job[];
    selectedJob: Job | null;
    pagination: unknown;
    initialize(jobList: Job[], paginationData?: unknown): void;
    loadJobDetail(job: Job, options?: JobSelectionOptions): void;
    clearJobDetail(): void;
    loadMoreJobs(): void;
}

const initializedDocuments = new WeakSet<Document>();
const builderPreviewInitialHtml = new WeakMap<HTMLElement, string>();
const CONSTRAINED_JOB_DISCOVERY_QUERY = '(max-width: 767.98px)';

function decodePathSegment(value: string): string | null {
    try {
        return decodeURIComponent(value);
    } catch {
        return null;
    }
}

export function jobIdFromPath(pathname: string): string | null {
    const match = /(?:^|\/)jobs\/([^/]+)\/?$/.exec(pathname.split(/[?#]/, 1)[0] ?? '');
    if (!match) return null;
    return decodePathSegment(match[1]);
}

export function isJobDetailPath(pathname: string): boolean {
    return jobIdFromPath(pathname) !== null;
}

export function isJobResultsPath(pathname: string): boolean {
    return /(?:^|\/)jobs\/?$/.test(pathname.split(/[?#]/, 1)[0] ?? '');
}

function relativeUrl(url: URL): string {
    return `${url.pathname}${url.search}${url.hash}`;
}

function jobBoardPath(pathname: string): string | null {
    const path = pathname.split(/[?#]/, 1)[0] ?? '';
    const match = /^(.*)\/jobs(?:\/[^/]+)?\/?$/.exec(path);
    return match ? (match[1] || '/') : null;
}

function isSameJobBoard(leftPathname: string, rightPathname: string): boolean {
    const leftBoard = jobBoardPath(leftPathname);
    const rightBoard = jobBoardPath(rightPathname);
    return leftBoard !== null && leftBoard === rightBoard;
}

function trustedDetailUrl(candidate: string, current: URL): URL | null {
    try {
        const detail = new URL(candidate, current);
        if (
            detail.origin !== current.origin
            || !isJobDetailPath(detail.pathname)
            || !isSameJobBoard(current.pathname, detail.pathname)
        ) return null;
        return detail;
    } catch {
        return null;
    }
}

function trustedResultsUrl(candidate: string, current: URL): URL | null {
    try {
        const results = new URL(candidate, current);
        if (
            results.origin !== current.origin
            || !isJobResultsPath(results.pathname)
            || !isSameJobBoard(current.pathname, results.pathname)
        ) return null;
        return results;
    } catch {
        return null;
    }
}

export function hydrationJobId(
    candidate: string,
    currentUrl: string,
    resultsUrl?: string
): string | null {
    try {
        const current = new URL(currentUrl);
        const authority = resultsUrl
            ? new URL(resultsUrl, current)
            : current;
        const publicDetail = trustedDetailUrl(candidate, authority);
        if (publicDetail) return jobIdFromPath(publicDetail.pathname);

        const detail = new URL(candidate, current);
        const currentVersionId = current.searchParams.get('versionId');
        const detailJobId = detail.searchParams.get('jobId');
        if (
            !/^\/acp\/partners\/[^/]+\/experience\/render$/u.test(current.pathname)
            || detail.origin !== current.origin
            || detail.pathname !== current.pathname
            || !currentVersionId
            || detail.searchParams.get('versionId') !== currentVersionId
            || detail.searchParams.get('pageKey') !== 'JOB_DETAIL'
            || !detailJobId
        ) return null;
        return detailJobId;
    } catch {
        return null;
    }
}

/**
 * Preserve the active result query when a card is promoted into the preview
 * route. The URL is still the canonical server route, so refresh/direct
 * navigation retains the published tenant and job scope.
 */
export function buildJobSelectionUrl(jobUrl: string, currentUrl: string): string {
    try {
        const current = new URL(currentUrl, 'https://fernain.invalid');
        const candidate = String(jobUrl ?? '').trim();
        if (!candidate) return relativeUrl(current);
        const detail = trustedDetailUrl(candidate, current);
        if (!detail) return relativeUrl(current);
        // Search, filter, and pagination state belongs to the board results
        // history entry. Keep it when selecting another card, including when
        // the current URL is already another detail route.
        if (isJobResultsPath(current.pathname) || isJobDetailPath(current.pathname)) {
            detail.search = current.search;
        }
        return relativeUrl(detail);
    } catch {
        return relativeUrl(new URL(currentUrl, 'https://fernain.invalid'));
    }
}

export function buildJobHistoryState(
    job: Pick<Job, 'id'>,
    currentUrl: string,
    scrollY = 0,
    resultsUrl?: string
): JobHistoryState {
    let resolvedResultsUrl = resultsUrl ?? currentUrl;
    try {
        const current = new URL(currentUrl, 'https://fernain.invalid');
        resolvedResultsUrl = relativeUrl(new URL(resultsUrl ?? currentUrl, current));
    } catch {
        // Keep the caller-provided URL when it is not URL-shaped.
    }
    return {
        fjJobDetail: true,
        jobId: String(job.id),
        resultsUrl: resolvedResultsUrl,
        scrollY: Number.isFinite(scrollY) ? Math.max(0, scrollY) : 0,
    };
}

export function decodeJobPayload(payload: string): Job | null {
    try {
        const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
        const padding = '='.repeat((4 - (normalized.length % 4)) % 4);
        const bytes = Uint8Array.from(atob(normalized + padding), (character) => character.charCodeAt(0));
        const parsed = JSON.parse(new TextDecoder().decode(bytes)) as Partial<Job> | null;
        if (
            !parsed
            || (typeof parsed.id !== 'number' && typeof parsed.id !== 'string')
            || typeof parsed.title !== 'string'
            || typeof parsed.company !== 'string'
            || typeof parsed.location !== 'string'
            || typeof parsed.description !== 'string'
        ) {
            return null;
        }
        return parsed as Job;
    } catch {
        return null;
    }
}

function targetElement(target: EventTarget | null): HTMLElement | null {
    if (!target || typeof target !== 'object') return null;
    const candidate = target as { closest?: unknown };
    return typeof candidate.closest === 'function' ? target as HTMLElement : null;
}

export function isIndependentJobAction(target: EventTarget | null): boolean {
    const element = targetElement(target);
    if (!element) return false;
    if (element.closest('[data-fj-job-independent-action]')) return true;
    const interactive = element.closest('a[href], button, input, select, textarea, summary, [role="link"]');
    if (!interactive) return false;
    return !interactive.closest('[data-fj-job-select]');
}

function jobCardFromTarget(target: EventTarget | null): HTMLElement | null {
    return targetElement(target)?.closest<HTMLElement>('[data-fj-job-load-on-click]') ?? null;
}

function jobFromElement(element: HTMLElement | null): Job | null {
    const payload = element?.dataset.fjJobPayload;
    return payload ? decodeJobPayload(payload) : null;
}

function findCardByJobId(root: Document, id: string): HTMLElement | null {
    const cards = root.querySelectorAll<HTMLElement>('[data-fj-job-load-on-click]');
    for (const card of Array.from(cards)) {
        if (card.dataset.fjJobId === id) return card;
        const job = jobFromElement(card);
        if (job && String(job.id) === id) return card;
    }
    return null;
}

export function refreshJobPayloadControls(root?: Document): void {
    const documentRoot = root ?? (typeof document !== 'undefined' ? document : null);
    if (!documentRoot) return;
    hydrateBuilderJobCards(
        documentRoot,
        documentRoot.defaultView ?? (typeof window !== 'undefined' ? window : null)
    );
}

function hydrateBuilderJobCards(root: Document, runtimeWindow: Window | null): void {
    const builderResults = root.querySelector<HTMLElement>('[data-fj-builder-job-results]');
    const preview = root.querySelector<HTMLElement>('[data-fj-builder-job-preview]');
    if (!builderResults || !preview || !runtimeWindow) return;
    const currentUrl = runtimeWindow.location.href;
    const resultsUrl = root
        .querySelector<HTMLElement>('[data-fj-job-discovery]')
        ?.dataset.fjJobResultsUrl;
    if (!builderPreviewInitialHtml.has(preview)) {
        builderPreviewInitialHtml.set(preview, preview.innerHTML);
    }

    const sources = new Map<string, HTMLElement>();
    root.querySelectorAll<HTMLElement>('[data-fj-job-payload-source]').forEach((source) => {
        const id = source.dataset.fjJobId;
        const payload = jobFromElement(source);
        const sourceUrl = String(payload?.url ?? '').trim();
        const trustedJobId = sourceUrl
            ? hydrationJobId(sourceUrl, currentUrl, resultsUrl)
            : null;
        if (
            id
            && payload
            && String(payload.id) === id
            && trustedJobId === id
        ) sources.set(id, source);
    });

    builderResults.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((anchor) => {
        const id = hydrationJobId(anchor.href, currentUrl, resultsUrl);
        const source = id ? sources.get(id) : null;
        const card = anchor.closest<HTMLElement>('article');
        if (!id || !source || !card) return;

        card.dataset.fjJobLoadOnClick = '';
        card.dataset.fjJobListitem = '';
        card.dataset.fjJobId = id;
        card.dataset.fjJobPayload = source.dataset.fjJobPayload ?? '';
        card.dataset.fjJobSelected = 'false';
        anchor.dataset.fjJobSelect = '';
        anchor.setAttribute('aria-controls', 'fj-job-preview');
        anchor.setAttribute('aria-current', 'false');
    });
}

function mountBuilderJobDetail(root: Document, id: string): boolean {
    const preview = root.querySelector<HTMLElement>('[data-fj-builder-job-preview]');
    if (!preview) return true;
    const templates = root.querySelectorAll<HTMLTemplateElement>(
        'template[data-fj-builder-job-detail-source]'
    );
    const source = Array.from(templates).find((template) => (
        template.dataset.fjJobId === id
    ));
    if (!source) return false;
    const pageOwnedFooter = preview.querySelector<HTMLElement>('[data-fj-page-owned-footer]');
    preview.innerHTML = source.innerHTML;
    if (pageOwnedFooter) preview.append(pageOwnedFooter);
    return true;
}

function restoreBuilderJobDetail(root: Document): void {
    const preview = root.querySelector<HTMLElement>('[data-fj-builder-job-preview]');
    const initialHtml = preview ? builderPreviewInitialHtml.get(preview) : null;
    if (preview && initialHtml !== undefined && initialHtml !== null) {
        preview.innerHTML = initialHtml;
    }
}

function syncSelectionState(root: Document, selectedId: string | null): void {
    root.querySelectorAll<HTMLElement>('[data-fj-job-load-on-click]').forEach((card) => {
        const cardId = card.dataset.fjJobId ?? jobFromElement(card)?.id;
        const selected = selectedId !== null && String(cardId) === selectedId;
        card.classList.toggle('is-selected', selected);
        card.dataset.fjJobSelected = String(selected);
        card.querySelectorAll<HTMLElement>('[data-fj-job-select]').forEach((control) => {
            control.setAttribute('aria-current', String(selected));
        });
    });

    const discovery = root.querySelector<HTMLElement>('[data-fj-job-discovery]');
    discovery?.classList.toggle('has-job-selection', selectedId !== null);

    const preview = root.querySelector<HTMLElement>('[data-fj-job-preview]');
    if (preview) {
        if (selectedId) preview.dataset.selectedJobId = selectedId;
        else delete preview.dataset.selectedJobId;
        preview.setAttribute('aria-busy', 'false');
        const detail = preview.querySelector<HTMLElement>('[data-fj-job-detail-component]');
        if (detail) {
            if (selectedId) detail.dataset.fjJobDetailId = selectedId;
            else delete detail.dataset.fjJobDetailId;
        }
        const title = preview.querySelector<HTMLElement>('[data-fj-job-detail-title]');
        if (selectedId && title) preview.setAttribute('aria-labelledby', title.id || 'fj-job-detail-title');
        else preview.removeAttribute('aria-labelledby');
    }
}

function syncPreviewBackLinks(root: Document, runtimeWindow: Window, resultsUrl: string): void {
    const trusted = trustedResultsUrl(resultsUrl, new URL(runtimeWindow.location.href));
    if (!trusted) return;
    const href = relativeUrl(trusted);
    root.querySelectorAll<HTMLAnchorElement>('[data-fj-preview-back]').forEach((link) => {
        link.setAttribute('href', href);
    });
}

export function shouldFocusJobPreview(runtimeWindow: Window | null, keyboardSelection = false): boolean {
    if (keyboardSelection) return true;
    return runtimeWindow?.matchMedia?.(CONSTRAINED_JOB_DISCOVERY_QUERY).matches ?? false;
}

function afterPreviewMount(runtimeWindow: Window | null, callback: () => void): void {
    if (runtimeWindow && typeof runtimeWindow.requestAnimationFrame === 'function') {
        runtimeWindow.requestAnimationFrame(() => callback());
        return;
    }
    if (runtimeWindow && typeof runtimeWindow.setTimeout === 'function') {
        runtimeWindow.setTimeout(callback, 0);
        return;
    }
    callback();
}

function focusPreview(root: Document, runtimeWindow: Window | null): void {
    const preview = root.querySelector<HTMLElement>('[data-fj-job-preview]');
    if (!preview) return;
    preview.focus({ preventScroll: true });
    if (shouldFocusJobPreview(runtimeWindow) && typeof preview.scrollIntoView === 'function') {
        preview.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
}

function scrollYForWindow(runtimeWindow: Window): number {
    return Number.isFinite(runtimeWindow.scrollY) ? runtimeWindow.scrollY : 0;
}

function restoreScroll(runtimeWindow: Window, scrollY: unknown): void {
    if (typeof scrollY !== 'number' || !Number.isFinite(scrollY)) return;
    const restore = (): void => {
        if (typeof runtimeWindow.scrollTo === 'function') runtimeWindow.scrollTo({ top: Math.max(0, scrollY), behavior: 'auto' });
    };
    if (typeof runtimeWindow.setTimeout === 'function') runtimeWindow.setTimeout(restore, 0);
    else restore();
}

export function initializeJobPayloadControls(
    store: Pick<JobLoadingStore, 'loadJobDetail' | 'clearJobDetail'>,
    root?: Document
): void {
    const documentRoot = root ?? (typeof document !== 'undefined' ? document : null);
    if (!documentRoot) return;
    if (initializedDocuments.has(documentRoot)) return;
    initializedDocuments.add(documentRoot);
    const runtimeWindow = documentRoot.defaultView ?? (typeof window !== 'undefined' ? window : null);
    let focusReturnTarget: HTMLElement | null = null;

    refreshJobPayloadControls(documentRoot);

    const load = (element: HTMLElement | null, options: JobSelectionOptions = {}): Job | null => {
        const job = jobFromElement(element);
        if (!job) return null;
        if (!mountBuilderJobDetail(documentRoot, String(job.id))) return null;
        store.loadJobDetail(job, options);
        syncSelectionState(documentRoot, String(job.id));
        afterPreviewMount(runtimeWindow, () => {
            syncSelectionState(documentRoot, String(job.id));
            if (options.focusPreview) focusPreview(documentRoot, runtimeWindow);
        });
        return job;
    };

    const selectFromCard = (card: HTMLElement, options: JobSelectionOptions = {}): void => {
        const job = jobFromElement(card);
        if (!job) return;

        const pushHistory = options.pushHistory !== false;
        const canTrackHistory = pushHistory && runtimeWindow && (
            isJobResultsPath(runtimeWindow.location.pathname)
            || isJobDetailPath(runtimeWindow.location.pathname)
        );
        let currentUrl: string | null = null;
        let rawJobUrl = '';
        if (canTrackHistory && runtimeWindow) {
            currentUrl = runtimeWindow.location.href;
            rawJobUrl = String(job.url ?? '').trim();
            const currentAbsoluteUrl = new URL(currentUrl);
            if (rawJobUrl && !trustedDetailUrl(rawJobUrl, currentAbsoluteUrl)) return;
        }
        if (!load(card, options)) return;
        if (canTrackHistory && runtimeWindow && currentUrl) {
            if (runtimeWindow.history && typeof runtimeWindow.history.pushState === 'function') {
                const currentState = runtimeWindow.history.state as Partial<JobHistoryState> | null;
                const targetUrl = buildJobSelectionUrl(rawJobUrl, currentUrl);
                const currentRelativeUrl = relativeUrl(new URL(currentUrl));
                const resultUrl = currentState?.fjJobDetail && typeof currentState.resultsUrl === 'string'
                    ? currentState.resultsUrl
                    : currentRelativeUrl;
                const historyState = buildJobHistoryState(
                    job,
                    currentRelativeUrl,
                    scrollYForWindow(runtimeWindow),
                    resultUrl
                );
                syncPreviewBackLinks(documentRoot, runtimeWindow, historyState.resultsUrl);
                if (targetUrl === currentRelativeUrl) {
                    return;
                }
                if (!currentState?.fjJobDetail && typeof runtimeWindow.history.replaceState === 'function') {
                    const resultState: JobResultsHistoryState = {
                        fjJobResults: true,
                        scrollY: scrollYForWindow(runtimeWindow),
                    };
                    runtimeWindow.history.replaceState(
                        { ...(currentState ?? {}), ...resultState },
                        '',
                        currentRelativeUrl
                    );
                }
                if (
                    currentState?.fjJobDetail
                    && typeof runtimeWindow.history.replaceState === 'function'
                ) {
                    // Detail selection is one transient layer over the preserved
                    // results entry: A → B → Back must return to those results.
                    runtimeWindow.history.replaceState(historyState, '', targetUrl);
                } else {
                    runtimeWindow.history.pushState(historyState, '', targetUrl);
                }
            }
        }
    };

    documentRoot.querySelectorAll<HTMLElement>('[data-fj-job-load-on-init]')
        .forEach((element) => load(element, { pushHistory: false }));

    documentRoot.addEventListener('click', (event) => {
        const previewBack = targetElement(event.target)?.closest<HTMLElement>('[data-fj-preview-back]');
        const currentState = runtimeWindow?.history.state as Partial<JobHistoryState> | null;
        if (previewBack && currentState?.fjJobDetail && runtimeWindow?.history) {
            event.preventDefault();
            runtimeWindow.history.back();
            return;
        }
        if (event.defaultPrevented || isIndependentJobAction(event.target)) return;
        const card = jobCardFromTarget(event.target);
        if (!card) return;
        const job = jobFromElement(card);
        if (!job) return;
        focusReturnTarget = targetElement(event.target)?.closest<HTMLElement>('[data-fj-job-select]')
            ?? card.querySelector<HTMLElement>('[data-fj-job-select]');
        event.preventDefault();
        selectFromCard(card, {
            focusPreview: shouldFocusJobPreview(runtimeWindow),
        });
    });

    documentRoot.addEventListener('keydown', (event) => {
        const keyboardEvent = event as KeyboardEvent;
        if (keyboardEvent.key !== 'Enter' && keyboardEvent.key !== ' ') return;
        if (isIndependentJobAction(keyboardEvent.target)) return;
        const card = jobCardFromTarget(keyboardEvent.target);
        if (!card || !jobFromElement(card)) return;
        focusReturnTarget = targetElement(keyboardEvent.target)?.closest<HTMLElement>('[data-fj-job-select]')
            ?? card.querySelector<HTMLElement>('[data-fj-job-select]');
        keyboardEvent.preventDefault();
        selectFromCard(card, { focusPreview: true });
    });

    runtimeWindow?.addEventListener('popstate', (event) => {
        const detailId = jobIdFromPath(runtimeWindow.location.pathname);
        if (detailId) {
            const state = event.state as Partial<JobHistoryState> | null;
            const card = findCardByJobId(documentRoot, detailId);
            if (card) {
                const loaded = load(card, {
                    pushHistory: false,
                    focusPreview: shouldFocusJobPreview(runtimeWindow),
                });
                if (loaded && typeof state?.resultsUrl === 'string') {
                    syncPreviewBackLinks(documentRoot, runtimeWindow, state.resultsUrl);
                }
                if (!loaded) {
                    store.clearJobDetail();
                    restoreBuilderJobDetail(documentRoot);
                    syncSelectionState(documentRoot, null);
                }
            }
            else {
                // Do not leave an unrelated detail visible when the target
                // is outside the current filtered/page-scoped result set.
                store.clearJobDetail();
                restoreBuilderJobDetail(documentRoot);
                syncSelectionState(documentRoot, null);
            }
            return;
        }

        store.clearJobDetail();
        restoreBuilderJobDetail(documentRoot);
        syncSelectionState(documentRoot, null);
        const state = event.state as Partial<JobHistoryState & JobResultsHistoryState> | null;
        restoreScroll(runtimeWindow, state?.scrollY);
        const returnTarget = focusReturnTarget;
        afterPreviewMount(runtimeWindow, () => {
            if (returnTarget && typeof returnTarget.focus === 'function') {
                returnTarget.focus({ preventScroll: true });
            }
        });
    });

    const constrainedViewport = runtimeWindow?.matchMedia?.(CONSTRAINED_JOB_DISCOVERY_QUERY);
    constrainedViewport?.addEventListener?.('change', (event) => {
        const preview = documentRoot.querySelector<HTMLElement>('[data-fj-job-preview]');
        const selectedId = preview?.dataset.selectedJobId;
        if (!preview || !selectedId) return;
        syncSelectionState(documentRoot, selectedId);
        if (!event.matches) return;
        afterPreviewMount(runtimeWindow, () => focusPreview(documentRoot, runtimeWindow));
    });
}

export default function JobLoadingManager(): JobLoadingStore {
    console.log('JobLoadingManager initialized');
    return {
        jobs: [] as Job[],
        selectedJob: null as Job | null,
        pagination: {} as unknown,

        initialize(jobList: Job[], paginationData?: unknown): void {
            this.jobs = jobList;
            this.pagination = paginationData ?? {};
        },

        loadJobDetail(job: Job, _options: JobSelectionOptions = {}): void {
            this.selectedJob = job;
        },

        clearJobDetail(): void {
            this.selectedJob = null;
        },

        loadMoreJobs(): void {
            // Pagination remains server-owned. This hook is intentionally inert
            // until a governed collection endpoint is introduced.
        },
    };
}
